import { useMemo } from 'react'
import { Stack, Group, Badge, Skeleton, Alert } from '@mantine/core'
import { AreaChart } from '@mantine/charts'
import { IconAlertCircle, IconChartArea } from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { databaseApi } from '@/api/database'
import { WidgetCard, CardHeader, EmptyState } from '@/components/ui'

interface MetricsTrendChartProps {
  title: string
  metric: 'cpu' | 'storage' | 'connections'
  tenantId: number
  dbId: string
  accentColor?: 'primary' | 'warning' | 'danger'
}

interface ChartDataPoint {
  ts: string
  actual: number | null
  forecast: number | null
}

const METRIC_UNIT: Record<string, string> = {
  cpu: '%',
  storage: '%',
  connections: '',
}

const METRIC_DB_NAME: Record<string, string> = {
  cpu: 'cpu_avg_pct',
  storage: 'storage_used_pct',
  connections: 'connections_used_pct',
}

export function buildForecastPoints(
  lastTs: string,
  lastValue: number,
  growthRatePerDay: number,
  daysUntilThreshold: number,
): ChartDataPoint[] {
  if (daysUntilThreshold <= 0) return []
  const forecastDays = Math.min(daysUntilThreshold, 30)
  const points: ChartDataPoint[] = []
  const startDate = new Date(lastTs)

  for (let i = 1; i <= forecastDays; i++) {
    const d = new Date(startDate)
    d.setDate(d.getDate() + i)
    points.push({
      ts: d.toISOString().split('T')[0],
      actual: null,
      forecast: Math.round((lastValue + growthRatePerDay * i) * 100) / 100,
    })
  }
  return points
}

export function MetricsTrendChart({
  title,
  metric,
  tenantId,
  dbId,
  accentColor = 'primary',
}: MetricsTrendChartProps) {
  const historyQuery = useQuery({
    queryKey: ['metrics-history', tenantId, dbId, metric],
    queryFn: () => databaseApi.getMetricsHistory(tenantId, dbId, METRIC_DB_NAME[metric] ?? metric, 30),
    enabled: !!tenantId && !!dbId,
    staleTime: 60_000,
  })

  const forecastQuery = useQuery({
    queryKey: ['capacity-forecast', tenantId, dbId],
    queryFn: () => databaseApi.getCapacityForecast(tenantId, dbId),
    enabled: !!tenantId && !!dbId,
    staleTime: 120_000,
  })

  const isLoading = historyQuery.isLoading || forecastQuery.isLoading
  const error = historyQuery.error || forecastQuery.error

  const forecast = forecastQuery.data?.[metric]
  const unit = METRIC_UNIT[metric]

  const chartData = useMemo<ChartDataPoint[]>(() => {
    const points = historyQuery.data?.points
    if (!points?.length) return []

    const realPoints: ChartDataPoint[] = points.map((p) => ({
      ts: p.ts.split('T')[0],
      actual: p.value,
      forecast: null,
    }))

    if (
      !forecast ||
      forecast.days_until_threshold == null ||
      forecast.days_until_threshold <= 0 ||
      forecast.growth_rate_per_day == null
    ) {
      return realPoints
    }

    const lastPoint = points[points.length - 1]
    // Bridge: last real point also gets forecast value for continuity
    realPoints[realPoints.length - 1].forecast = lastPoint.value

    const forecastPoints = buildForecastPoints(
      lastPoint.ts,
      lastPoint.value,
      forecast.growth_rate_per_day,
      forecast.days_until_threshold,
    )

    return [...realPoints, ...forecastPoints]
  }, [historyQuery.data, forecast])

  return (
    <WidgetCard accent={accentColor}>
      <CardHeader title={title} />
      <Stack gap="sm" p="md">
        {isLoading ? (
          <>
            <Group gap="xs">
              <Skeleton width={80} height={28} />
              <Skeleton width={80} height={28} />
              <Skeleton width={80} height={28} />
            </Group>
            <Skeleton height={250} />
          </>
        ) : error ? (
          <Alert
            icon={<IconAlertCircle size={16} />}
            title="Erro ao carregar dados"
            color="red"
          >
            {(error as Error).message ?? 'Erro desconhecido'}
          </Alert>
        ) : (
          <>
            {/* KPI badges */}
            {forecast && (
              <Group gap="xs">
                <Badge variant="light" color="blue" size="lg">
                  Atual: {forecast.current_pct != null ? `${forecast.current_pct.toFixed(1)}${unit}` : '—'}
                </Badge>
                <Badge variant="light" color="orange" size="lg">
                  {forecast.growth_rate_per_day != null
                    ? `+${forecast.growth_rate_per_day.toFixed(2)}${unit}/dia`
                    : 'sem histórico'}
                </Badge>
                <Badge
                  variant="light"
                  color={
                    forecast.days_until_threshold != null && forecast.days_until_threshold <= 7
                      ? 'red'
                      : 'gray'
                  }
                  size="lg"
                >
                  {forecast.days_until_threshold == null
                    ? 'sem previsão'
                    : forecast.days_until_threshold > 0
                      ? `${forecast.days_until_threshold}d até limite`
                      : 'Limite atingido'}
                </Badge>
              </Group>
            )}

            {/* Chart */}
            {chartData.length > 0 ? (
              <AreaChart
                h={250}
                data={chartData}
                dataKey="ts"
                series={[
                  { name: 'actual', color: 'teal.6', label: 'Real' },
                  { name: 'forecast', color: 'orange.5', label: 'Previsão', strokeDasharray: '4 4' },
                ]}
                curveType="monotone"
                withDots={false}
                withTooltip
                tooltipAnimationDuration={200}
                areaChartProps={{
                  syncId: `capacity-${metric}`,
                }}
                referenceLines={
                  metric !== 'connections'
                    ? [{ y: 80, color: 'red.5', label: 'Threshold 80%' }]
                    : undefined
                }
              />
            ) : (
              <EmptyState
                bare
                size="sm"
                accent="info"
                icon={IconChartArea}
                title="Sem dados disponíveis"
                description="Aguardando coleta de métricas para esta instância e período."
              />
            )}
          </>
        )}
      </Stack>
    </WidgetCard>
  )
}
